import React, { useState, useEffect } from 'react';
import styles from './OnboardingModal.module.css';

interface OnboardingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface OnboardingStep {
  title: string;
  content: string;
  image?: string;
  icon: string;
}

const onboardingSteps: OnboardingStep[] = [
  {
    title: "Welcome, Agent",
    content: "You've been recruited as a Chess Intelligence Agent. Your mission: decode encrypted messages hidden in chess notation. The fate of the chess world depends on your skills.",
    icon: "🕵️",
  },
  {
    title: "Your Training Begins",
    content: "Start as a Rookie Agent learning board coordinates. Progress through Field Agent (basic moves), Special Agent (sequences), and finally Master Spy (full game analysis).",
    icon: "🎖️",
  },
  {
    title: "Mission Structure",
    content: "Each mission tests your chess knowledge. Complete briefings, execute challenges, and review results. Your performance determines your agent ranking and unlocks new operations.",
    icon: "📋",
  },
  {
    title: "Spaced Repetition Learning",
    content: "Our advanced learning system tracks your progress. Difficult concepts appear more frequently until mastered. Your brain is your most powerful weapon.",
    icon: "🧠",
  },
  {
    title: "Daily Intel Operations",
    content: "Return daily for personalized training sessions. The system adapts to your strengths and weaknesses, ensuring optimal skill development.",
    icon: "📡",
  },
  {
    title: "Ready for Deployment",
    content: "Your training begins now. Remember: precision, speed, and pattern recognition are key. Good luck, Agent. The chess world is counting on you.",
    icon: "🚀",
  },
];

const OnboardingModal: React.FC<OnboardingModalProps> = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setCurrentStep(0);
    }
  }, [isOpen]);

  const handleNext = () => {
    if (currentStep < onboardingSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleClose();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const handleSkip = () => {
    handleClose();
  };

  if (!isOpen) return null;

  const currentStepData = onboardingSteps[currentStep];
  const isLastStep = currentStep === onboardingSteps.length - 1;

  return (
    <div className={`${styles.overlay} ${isVisible ? styles.visible : ''}`}>
      <div className={`${styles.modal} ${isVisible ? styles.modalVisible : ''}`}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <div className={styles.icon}>{currentStepData.icon}</div>
            <div className={styles.headerText}>
              <h2 className={styles.title}>{currentStepData.title}</h2>
              <div className={styles.stepIndicator}>
                Step {currentStep + 1} of {onboardingSteps.length}
              </div>
            </div>
          </div>
          <button 
            className={styles.closeButton}
            onClick={handleClose}
            aria-label="Close onboarding"
          >
            ✕
          </button>
        </div>

        {/* Progress Bar */}
        <div className={styles.progressContainer}>
          <div className={styles.progressBar}>
            <div 
              className={styles.progressFill}
              style={{ width: `${((currentStep + 1) / onboardingSteps.length) * 100}%` }}
            />
          </div>
          <div className={styles.progressText}>
            {Math.round(((currentStep + 1) / onboardingSteps.length) * 100)}% Complete
          </div>
        </div>

        {/* Content */}
        <div className={styles.content}>
          <div className={styles.stepContent}>
            <p className={styles.description}>{currentStepData.content}</p>
          </div>

          {/* Step Indicators */}
          <div className={styles.stepIndicators}>
            {onboardingSteps.map((_, index) => (
              <button
                key={index}
                className={`${styles.stepDot} ${
                  index === currentStep ? styles.stepDotActive : ''
                } ${index < currentStep ? styles.stepDotCompleted : ''}`}
                onClick={() => setCurrentStep(index)}
                aria-label={`Go to step ${index + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <div className={styles.footerLeft}>
            <button 
              className={styles.skipButton}
              onClick={handleSkip}
            >
              Skip Tutorial
            </button>
          </div>
          
          <div className={styles.footerRight}>
            {currentStep > 0 && (
              <button 
                className={styles.previousButton}
                onClick={handlePrevious}
              >
                Previous
              </button>
            )}
            <button 
              className={styles.nextButton}
              onClick={handleNext}
            >
              {isLastStep ? 'Begin Mission' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingModal;